<template>
  <custom-tab
    content-class="mt-3"
    nav-class="nav-vue-underline"
    no-nav-style
    @input="onChangeTab"
  >
    <custom-tab-item title="Información Adicional" :active="isAdditionalInfo">
      <div v-if="hasAdditionalInfo">
        <additional-information :item="referral" />
      </div>
    </custom-tab-item>

    <custom-tab-item
      title="Información de Escuela Actual"
      :active="isActualSchoolInfo"
    >
      <div v-if="hasSchoolInfo" class="px-md-3">
        <div class="mt-md-4">
          <div class="d-flex align-items-center mb-2">
            <i class="fas fa-info-circle mr-2 text-orange"></i>
            <div class="color-gray-7 font-16 font-weight-bold">
              Información de Escuela Actual
            </div>
          </div>
          <div class="ml-4">
            <div class="row">
              <div class="col-lg-3 col-md-6 col-12">
                <span class="data-title">CSEE:</span>
                <p>{{ actualSchoolInfo.centerName | empty }}</p>
              </div>
              <div class="col-lg-3 col-md-6 col-12">
                <span class="data-title">Región:</span>
                <p>{{ actualSchoolInfo.regionName | empty }}</p>
              </div>
              <div class="col-lg-3 col-md-6 col-12">
                <span class="data-title">Municipio:</span>
                <p>{{ actualSchoolInfo.municipalityName | empty }}</p>
              </div>
              <div class="col-lg-3 col-md-6 col-12">
                <span class="data-title">Escuela:</span>
                <p>{{ actualSchoolInfoSchoolName | empty }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </custom-tab-item>

    <custom-tab-item title="Documentos" :active="isDocumentTab">
      <div class="bg-white rounded mt-md-4">
        <documents :referral="referral" />
      </div>
    </custom-tab-item>

    <custom-tab-item
      title="Documentos de Uso e Inventario de Equipo"
      :active="isUsageDocumentsTab"
    >
      <div class="bg-white rounded mt-md-4">
        <equipment-use-inventory-documents :referral="referral" />
      </div>
    </custom-tab-item>

    <div class="px-md-3">
      <custom-tab-item title="Historial de Estatus" :active="isStatusHisTab">
        <div class="bg-white rounded mt-md-4">
          <custom-history :items="statusLog" />
        </div>
      </custom-tab-item>
      <custom-tab-item title="Historial de Bitácora" :active="isBinnacleTab">
        <div class="bg-white rounded mt-md-4">
          <binnacle-history :history="binnacle.history" />
        </div>
      </custom-tab-item>

      <custom-tab-item
        title="Historial de Citas"
        :active="isAppointmentTab"
        v-if="isAppointmentVisible"
      >
        <div class="bg-white rounded mt-md-4">
          <appointment-timeline :items="appointmentLog" />
        </div>
      </custom-tab-item>
    </div>
  </custom-tab>
</template>

<script>
import { CustomTab, CustomTabItem } from 'custom-tabs';
import referralService from '@/services/api/referralsandequipments.api';
import { date } from '@/utils/filters';
import BinnacleHistory from './components/BinnacleHistory.vue';
import CustomHistory from '@quisit/mipe-custom-history';
import AppointmentTimeline from './components/AppointmentTimeline.vue';
import peiAtReferralTabs from '@/utils/constants/peiAtReferralTabs';
import Documents from './components/Documents.vue';
import AdditionalInformation from '../requisitionManagement/components/AdditionalInformation';
import EquipmentUseInventoryDocuments from './components/EquipmentUseInventoryDocuments.vue';

export default {
  name: 'ReferralTabs',
  components: {
    CustomTab,
    CustomTabItem,
    BinnacleHistory,
    CustomHistory,
    AppointmentTimeline,
    Documents,
    AdditionalInformation,
    EquipmentUseInventoryDocuments,
  },
  props: {
    referral: {
      type: Object,
      required: true,
    },
    tabIndex: {
      type: Number,
      default: peiAtReferralTabs.AdditionalInfo,
    },
  },
  data() {
    return {
      additionalInfo: {},
      actualSchoolInfo: {},
      statusLog: [],
      binnacle: {
        pageSize: 2,
        totalItemCount: 0,
        history: [],
      },
      appointmentLog: [],
    };
  },
  computed: {
    isAppointmentVisible() {
      return this.referral.hasAppointment === true;
    },
    isAdditionalInfo() {
      return this.tabIndex === peiAtReferralTabs.AdditionalInfo;
    },
    isActualSchoolInfo() {
      return this.tabIndex === peiAtReferralTabs.ActualSchoolInfo;
    },
    isDocumentTab() {
      return this.tabIndex === peiAtReferralTabs.Documents;
    },
    isAppointmentTab() {
      return this.tabIndex === peiAtReferralTabs.AppointmentHistory;
    },
    isBinnacleTab() {
      return this.tabIndex === peiAtReferralTabs.LogHistory;
    },
    isStatusHisTab() {
      return this.tabIndex === peiAtReferralTabs.StatusHistory;
    },
    hasAdditionalInfo() {
      return Boolean(Object.keys(this.additionalInfo).length);
    },
    hasSchoolInfo() {
      return Boolean(Object.keys(this.actualSchoolInfo).length);
    },
    additionalInfoSchoolName() {
      if (
        this.additionalInfo.additional?.schoolCode &&
        this.additionalInfo.additional?.schoolName
      )
        return `${this.additionalInfo.additional.schoolCode} - ${this.additionalInfo.additional.schoolName}`;
      return null;
    },
    additionalInfoServices() {
      if (this.additionalInfo.additional?.services?.length)
        return this.additionalInfo.additional.services.join(', ').trim();
      return null;
    },
    additionalInfoAppoimentDates() {
      if (this.additionalInfo.progressDates?.apppoimentDates?.length)
        return this.additionalInfo.progressDates.apppoimentDates
          .map((d) => date(d))
          .join(', ')
          .trim();
      return null;
    },
    actualSchoolInfoSchoolName() {
      if (this.actualSchoolInfo.schoolCode && this.actualSchoolInfo.schoolName)
        return `${this.actualSchoolInfo.schoolCode} - ${this.actualSchoolInfo.schoolName}`;
      return null;
    },
    isUsageDocumentsTab() {
      return this.tabIndex === peiAtReferralTabs.UsageDocuments;
    },
  },
  methods: {
    async getAdditionalInfo() {
      if (this.hasAdditionalInfo) return;
      const id = this.referral.id;
      const { data } = await referralService.getAdditionalInfo(id);
      this.additionalInfo = data;
    },
    async getActualSchoolInfo() {
      if (this.hasSchoolInfo) return;
      const id = this.referral.id;
      const { data } = await referralService.getActualSchoolInfo(id);
      this.actualSchoolInfo = data;
    },
    async getStatusLog() {
      const id = this.referral.id;
      const { data } = await referralService.getStatusLog(id);
      this.statusLog = data;
    },
    async getAppointmentLog() {
      const id = this.referral.id;
      const { data } = await referralService.getAppointmentLog(id);
      this.appointmentLog = data;
    },
    onChangeTab(key) {
      switch (key) {
        case peiAtReferralTabs.AdditionalInfo:
          this.getAdditionalInfo();
          break;
        case peiAtReferralTabs.ActualSchoolInfo:
          this.getActualSchoolInfo();
          break;
        case peiAtReferralTabs.StatusHistory:
          this.getStatusLog();
          break;
        case peiAtReferralTabs.LogHistory:
          this.binnacle.pageSize = 2;
          this.handlerLoadBinacle();
          break;
        case peiAtReferralTabs.AppointmentHistory:
          this.getAppointmentLog();
          break;
        default:
          break;
      }
      this.$emit('onTabChanged', key);
    },
    async handlerLoadBinacle() {
      const payload = {
        peiAtServiceId: this.referral.id,
      };
      const { data } = await referralService
        .getBinnacle(payload)
        .catch((error) => {
          this.ShowToast('Error', error?.response?.data.message, 'error');
        });
      if (data) {
        this.binnacle.history = data;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
